interface SkeletonProps {
  className?: string
}

/** Base skeleton with shimmer animation */
export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700 ${className}`}
      style={{
        background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
      }}
    />
  )
}

/** Product card skeleton matching ProductCard layout */
export function ProductCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
      {/* Image placeholder */}
      <div className="aspect-[4/3] bg-gray-100">
        <Skeleton className="w-full h-full rounded-none" />
      </div>
      {/* Text lines */}
      <div className="p-3 space-y-2.5">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-3 w-2/3" />
        <div className="flex gap-2 pt-1">
          <Skeleton className="h-8 flex-1" />
          <Skeleton className="h-8 flex-1" />
        </div>
      </div>
    </div>
  )
}

/** Profile page skeleton */
export function ProfileSkeleton() {
  return (
    <div className="max-w-md mx-auto space-y-3">
      {/* Profile header */}
      <div className="-mx-4 -mt-2 mb-4 px-4 pt-6 pb-6 bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-400 rounded-b-[2rem]">
        <div className="flex items-center gap-4">
          <Skeleton className="w-16 h-16 rounded-2xl bg-white/30" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-32 bg-white/30" />
            <Skeleton className="h-4 w-24 bg-white/20" />
          </div>
          <Skeleton className="w-10 h-10 rounded-xl bg-white/20" />
        </div>
      </div>
      {/* Section cards */}
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-white rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-2.5 mb-3">
            <Skeleton className="w-9 h-9 rounded-xl" />
            <Skeleton className="h-4 w-28" />
          </div>
          <Skeleton className="h-20 w-full" />
        </div>
      ))}
    </div>
  )
}

/** Inline shimmer keyframes */
export function SkeletonStyles() {
  return (
    <style>{`
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
    `}</style>
  )
}

/** Helper: render N skeleton cards in a grid */
export function SkeletonGrid({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  )
}
